<script setup lang="ts">
    import log from '~/utils/log';

    const { urlSegment } = useLanguage();
    const { t } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const { shopwareDown } = useShopwareDowntime();

    const { userShopware } = useLoginState();

    const productHidden = ref(false);
    const CATEGORY_EXCLUSIVE = t('listing.exclusiveCategoryName');

    const routeSegments = computed(() => {
        const all = route.params.all;
        if (Array.isArray(all)) return all;
        return typeof all === 'string' ? [all] : [];
    });

    const { rawPath } = useCleanStoryblokPath();
    const { data: story } = await useStory<PagePdpStoryblok>(rawPath, null, { disableFatalErrors: true });
    useStoryblokBridgeRef(story);

    const canonicalLink = computed(() => {
        return story.value?.content?.canonicalLink;
    });

    const canonicalUrlAbsolute = useCanonicalUrl(canonicalLink);

    useLocalizedRedirectOnLangSwitch(story.value?.translated_slugs);

    const fallbackProductId = computed(() => routeSegments.value.at(-1) || '');
    const navigationId = computed(() => story.value?.content?.productId || fallbackProductId.value);
    const productAsyncKey = computed(() => `cmsProduct${navigationId.value}-${urlSegment.value}`);

    const { search } = useProductSearch();
    const { data: productResponse, error } = await useAsyncData(
        productAsyncKey,
        () =>
            search(navigationId.value, {
                criteria: {
                    includes: {
                        product: [
                            'id',
                            'name',
                            'translated',
                            'productNumber',
                            'stock',
                            'releaseDate',
                            'streamIds',
                            'optionIds',
                            'parentId',
                            'customFields',
                            'calculatedPrice',
                            'calculatedPrices',
                            'calculatedCheapestPrice',
                            'manufacturer',
                            'media',
                            'properties',
                            'categories',
                            'options',
                            'deliveryTime',
                            'ean'
                        ],
                        product_media: ['id', 'position', 'media'],
                        media: ['id', 'url', 'alt', 'title', 'fileName', 'mimeType', 'thumbnails'],
                        media_thumbnail: ['id', 'url', 'width', 'height'],
                        property_group_option: ['id', 'name', 'translated'],
                        category: ['id', 'name'],
                        product_manufacturer: ['id', 'name'],
                        delivery_time: ['id', 'name'],
                        calculated_price: [
                            'unitPrice',
                            'totalPrice',
                            'calculatedTaxes',
                            'taxRules',
                            'quantity',
                            'referencePrice',
                            'listPrice',
                            'regulationPrice'
                        ],
                        calculated_cheapest_price: [
                            'unitPrice',
                            'totalPrice',
                            'calculatedTaxes',
                            'taxRules',
                            'quantity',
                            'referencePrice',
                            'listPrice',
                            'regulationPrice',
                            'hasRange',
                            'variantId'
                        ]
                    },
                    associations: {
                        media: {
                            associations: {
                                media: {}
                            }
                        },
                        properties: {},
                        manufacturer: {},
                        categories: {},
                        options: {}
                    }
                }
            }),
        {
            watch: [navigationId]
        }
    );

    if (!productResponse.value || error.value) {
        if (error.value) log.error('[FrontendDetailPage.vue]', error.value);

        if (shopwareDown.value) {
            const homePath = `/${urlSegment.value}`;

            log.debug(
                '[FrontendDetailPage.vue] Product not found, but Shopware is down. Redirecting to home with shopwareDown query param.'
            );
            await navigateTo(
                {
                    path: homePath,
                    query: { ...route.query, shopwareDown: '1' },
                    hash: route.hash
                },
                { redirectCode: 302, replace: true }
            );
        } else {
            throw createError({
                statusCode: 404,
                statusMessage: 'Product not found'
            });
        }
    }

    const productSource = computed(() => productResponse.value?.product || null);
    const configuratorSource = computed(() => productResponse.value?.configurator || null);
    useProduct(productSource.value, configuratorSource.value);

    const isProductHiddenClient = useClientGate(
        computed(() => {
            if (!productSource.value) return false;
            return !isProductVisibleForUser(productSource.value, userShopware.value, CATEGORY_EXCLUSIVE);
        })
    );

    let redirectTimeout: ReturnType<typeof setTimeout> | null = null;

    watch(isProductHiddenClient, (newVal) => {
        if (redirectTimeout !== null) {
            clearTimeout(redirectTimeout);
            redirectTimeout = null;
        }

        if (newVal) {
            productHidden.value = true;

            redirectTimeout = setTimeout(() => {
                router.replace(`/${urlSegment.value}/shop`);
            }, 3000);
            return;
        }

        productHidden.value = false;
    });

    const hasRedirectedForShopwareDown = ref(false);

    const redirectToHomeWithToast = () => {
        if (hasRedirectedForShopwareDown.value) return;
        hasRedirectedForShopwareDown.value = true;

        const homePath = `/${urlSegment.value}`;

        log.debug('[FrontendDetailPage.vue] Shopware is down. Redirecting to home with shopwareDown query param.');
        return navigateTo(
            {
                path: homePath,
                query: { ...route.query, shopwareDown: '1' },
                hash: route.hash
            },
            { redirectCode: 302, replace: true }
        );
    };

    watch(
        shopwareDown,
        (down) => {
            if (!down) return;
            if (route.query.shopwareDown) return;

            redirectToHomeWithToast();
        },
        { immediate: true }
    );

    onBeforeUnmount(() => {
        if (redirectTimeout !== null) {
            clearTimeout(redirectTimeout);
            redirectTimeout = null;
        }
    });

    useCmsHead(productSource, { mainShopTitle: 'MEDICE Health Family Shop' });

    useHead(() => ({
        link: [{ rel: 'canonical', href: canonicalUrlAbsolute.value }]
    }));
</script>

<template>
    <div class="pdp">
        <ProductStatic
            v-if="productSource && !productHidden"
            :product="productSource"
            :storyblok-body="story?.content?.body"
            :storyblok-story="story?.content?.storyWrapper"
            :storyblok-custom-tabs="story?.content?.customTabs"
            :storyblok-recommendation="story?.content?.recommendation"
        />

        <div
            v-else-if="productHidden"
            class="product-hidden-message"
        >
            <img
                src="~/assets/images/404.svg"
                :alt="t('general.error')"
            />
            <span class="sorry">
                {{ t('general.sorry') }}
            </span>
            <p class="message">
                {{ t('listing.productHiddenMessage') }}
            </p>
            <div class="button-wrapper">
                <PrimaryButton
                    :button-text="t('listing.toShop')"
                    :href="`/${urlSegment}/shop`"
                />
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/detail-page/front-end-detail-page.scss';
</style>
